import { BidBattleImageWrapper } from "@/components/battle";
import { MolliePaymentForm } from "@/components/payments.form";
import { useAppForm } from "@/hooks/form";
import { usePayments } from "@/lib/backend/payments";
import { Radio as RadioPrimitive } from "@base-ui-components/react/radio";
import { RadioGroup as RadioGroupPrimitive } from "@base-ui-components/react/radio-group";
import { Button } from "@bidbattles/bloks/components/ui/button";
import { Divider } from "@bidbattles/bloks/components/ui/divider";
import { PageLoader } from "@bidbattles/bloks/components/ui/loading";
import {
	Tab,
	TabPanel,
	Tabs,
	TabsList,
} from "@bidbattles/bloks/components/ui/tabs";
// import { Drawer, DrawerContent, DrawerTitle } from "@/components/ui/drawer";
// import { useMininumDeviceWidthBreakpoint } from "@/hooks/media";
import {
	BBBackendClient,
	BBBackendQueryKeys,
	// BBBackendQueryKeys,
	BBBackendQueryOptions,
	type BidPackage,
	type CheckoutBidItem,
} from "@bidbattles/bloks/lib/rapace";
import { cn } from "@bidbattles/bloks/lib/ui";
import { calculateVAT, formatCurrency } from "@bidbattles/bloks/lib/utils";
import {
	type QueryClient,
	QueryErrorResetBoundary,
	useMutation,
	useQueryClient,
	useSuspenseQuery,
} from "@tanstack/react-query";
import {
	ClientOnly,
	type NavigateFn,
	createFileRoute,
} from "@tanstack/react-router";
import { useStore } from "@tanstack/react-store";
import { LayoutGroup, motion } from "motion/react";
import { Suspense, useCallback, useState } from "react";
import { toast } from "sonner";
import { MinusCircle, PlusCircle } from "untitledui-js/react";
import { z } from "zod/v4";

// const RadioGroupPrimitive = motion.create(_RadioGroupPrimitive);
// const RadioPrimitiveIndicator = motion.create(RadioPrimitive.Indicator);

const searchSchema = z.object({
	rebuy: z.boolean().default(false),
});
export const Route = createFileRoute("/battle_/$id/bids")({
	async loader({ params, context }) {
		await Promise.allSettled([
			context.queryClient.ensureQueryData(
				BBBackendQueryOptions.listBidPricesForBattle({
					battle_id: params.id,
				}),
			),
			context.queryClient.ensureQueryData(
				BBBackendQueryOptions.listBidPackagesForBattle({
					battle_id: params.id,
				}),
			),
		]);
	},
	validateSearch: (params) => searchSchema.parse(params),
	component: RouteComponent,
});

type FormState = {
	package?: { package_id: string };
	individual?: { bid_items: Array<CheckoutBidItem> };
};

type Stage =
	| { stage: "buy"; type: keyof FormState; form?: FormState }
	| { stage: "payment"; type: keyof FormState; form: FormState };

type StageProps = {
	stage: Stage;
	backToForm: (form: FormState) => void;
	goToPayment: (form: FormState) => void;
};

function RouteComponent() {
	const { id } = Route.useParams();
	const { rebuy } = Route.useSearch();
	// const router = useRouter();
	// const isAtLeastMedium = useMininumDeviceWidthBreakpoint({
	// 	breakpoint: "md",
	// 	serverDefault: true,
	// });
	const { data: battle } = useSuspenseQuery(
		BBBackendQueryOptions.fetchBattle({ id }),
	);
	const image = battle.item_image_urls[0];
	const [stage, setStage] = useState<Stage>({
		stage: "buy",
		type: "package",
	});

	const goToPayment = useCallback((form: FormState) => {
		setStage((stage) => ({ ...stage, stage: "payment", form }));
	}, []);

	const backToForm = useCallback((form: FormState) => {
		setStage((stage) => ({ ...stage, stage: "buy", form }));
	}, []);

	const stageProps: StageProps = { stage, backToForm, goToPayment };

	return (
		// <Drawer
		// 	modal={false}
		// 	open={!isAtLeastMedium}
		// 	onOpenChange={() => !isAtLeastMedium && router.history.back()}
		// >
		// 	<DrawerContent>
		// 		<DrawerTitle className="mb-[1.875rem] text-xl">
		// 			Select a bid package
		// 		</DrawerTitle>
		// 		<Bids />
		// 	</DrawerContent>
		// </Drawer>
		<section className="w-full flex flex-col items-center py-box">
			<div className="max-w-container flex flex-col gap-y-8 px-box lg:grid grid-cols-2 lg:ps-box lg:pe-0 w-full">
				<div className="flex flex-col gap-5 items-stretch col-span-1">
					<h1 className="font-bold text-gray-900 text-xl">Enter battle</h1>
					<BidBattleImageWrapper className="w-full rounded-lg overflow-hidden">
						<motion.img
							layout
							layoutId={image}
							src={image}
							className="w-full object-cover aspect-[2/1]"
						/>
					</BidBattleImageWrapper>
					<div className="flex flex-col gap-3">
						<h2 className="font-bold text-lg leading-7 md:text-3xl md:leading-[2.375rem]">
							{battle.item_name}
						</h2>
						<p className="text-gray-900 mb-3 whitespace-pre-line">
							{battle.item_description}
						</p>
						{!rebuy && (
							<p className="">
								Entry price&nbsp;
								<span className="font-bold text-xl">
									{formatCurrency("EUR", battle.entry_price)}
								</span>
							</p>
						)}
					</div>
				</div>
				<div className="flex flex-col col-span-1">
					<div className="flex-col lg:px-box">
						<Tabs value={stage.stage}>
							<TabPanel value="buy">
								<Tabs
									value={stage.type}
									onValueChange={(type) => {
										switch (type) {
											case "package": {
												setStage((stage) => ({
													...stage,
													type: "package",
												}));
												break;
											}
											case "individual": {
												setStage((stage) => ({
													...stage,
													type: "individual",
												}));
												break;
											}
										}
									}}
								>
									<TabsList>
										<Tab value="package">Packages</Tab>
										<Tab value="individual">Individual</Tab>
									</TabsList>
									<TabPanel value="package" className="py-box">
										<p className="text-gray-900 mb-[1.875rem]">
											Select a bid package
										</p>
										<BidPackages {...stageProps} />
									</TabPanel>
									<TabPanel value="individual" className="py-box">
										<p className="text-gray-900 mb-[1.875rem]">
											Select your preferred bids
										</p>
										<Bids {...stageProps} />
									</TabPanel>
								</Tabs>
							</TabPanel>
							<ClientOnly>
								<TabPanel value="payment" className="md:py-box">
									<QueryErrorResetBoundary>
										<Suspense fallback={<PageLoader />}>
											<PaymentForm {...stageProps} />
										</Suspense>
									</QueryErrorResetBoundary>
								</TabPanel>
							</ClientOnly>
						</Tabs>
					</div>
				</div>
			</div>
		</section>
	);
}

async function handleSuccess({
	queryClient,
	rebuy,
	id,
	navigate,
}: {
	queryClient: QueryClient;
	rebuy: boolean;
	id: string;
	navigate: NavigateFn;
}) {
	await Promise.allSettled([
		queryClient.refetchQueries(BBBackendQueryOptions.fetchBattle({ id })),
	]);

	toast.success("Purchase successful.");
	if (rebuy) {
		navigate({
			from: Route.fullPath,
			to: "/battle/$id/battle",
			params: { id },
		});
	} else {
		navigate({
			from: Route.fullPath,
			to: "/battle/$id/details",
			params: { id },
		});
	}
}
function BidPackages({
	className,
	goToPayment,
	stage,
}: { className?: string } & StageProps) {
	const { id } = Route.useParams();
	const { rebuy } = Route.useSearch();

	const { data: battle } = useSuspenseQuery(
		BBBackendQueryOptions.fetchBattle({ id }),
	);
	const { data: bidPackages } = useSuspenseQuery(
		BBBackendQueryOptions.listBidPackagesForBattle({ battle_id: id }),
	);

	const form = useAppForm({
		defaultValues: {
			package_id:
				stage.form?.package?.package_id ??
				bidPackages.find((bidPackage) => bidPackage.is_included)?.id ??
				bidPackages[0]?.id ??
				"",
		},
		async onSubmit({ value }) {
			goToPayment({
				package: value,
			});
		},
	});
	const packageId = useStore(form.store, (state) => state.values.package_id);
	const bidPackage = bidPackages.find(
		(bidPackage) => bidPackage.id === packageId,
	);
	const bidPackagePrice =
		bidPackage?.is_included && !rebuy ? 0 : (bidPackage?.total_price ?? 0);
	const totalPriceExclVat = rebuy
		? bidPackagePrice
		: battle.entry_price + bidPackagePrice;

	return (
		<LayoutGroup id="bid-package-radio-indicator">
			<motion.form
				layout
				onSubmit={(e) => {
					e.preventDefault();
					form.handleSubmit();
				}}
				className="flex flex-col gap-y-12"
			>
				<form.AppField name="package_id">
					{(field) => (
						<RadioGroupPrimitive
							render={<motion.div layout />}
							value={field.state.value}
							onValueChange={(value) => field.handleChange(value as string)}
							className={cn("flex flex-col gap-4 items-stretch", className)}
						>
							{bidPackages.map((bidPackage) => (
								<BidPackageCard key={bidPackage.id} bidPackage={bidPackage} />
							))}
						</RadioGroupPrimitive>
					)}
				</form.AppField>
				<CheckoutSummary
					rebuy={rebuy}
					entryPrice={battle.entry_price}
					bidsPrice={bidPackagePrice}
					totalPriceExclVat={totalPriceExclVat}
				/>
				<form.AppForm>
					<form.SubscribeButton label="Check Out" />
				</form.AppForm>
			</motion.form>
		</LayoutGroup>
	);
}

function Bids({ stage, goToPayment }: { className?: string } & StageProps) {
	const { id } = Route.useParams();
	const { rebuy } = Route.useSearch();

	const { data: battle } = useSuspenseQuery(
		BBBackendQueryOptions.fetchBattle({ id }),
	);
	const { data: bidPrices } = useSuspenseQuery(
		BBBackendQueryOptions.listBidPricesForBattle({ battle_id: id }),
	);
	const uniqBids = new Map(bidPrices.map((price) => [price.id, price]));
	const formBidItems: [string, number][] | undefined =
		stage.form?.individual?.bid_items?.map((item) => [
			item.bid_price_id,
			item.quantity,
		]);
	const defaultBidItemValues = Object.fromEntries(
		formBidItems ??
			// Safari 17.4 doesn't support Iterator.map
			[...uniqBids.entries()].map(([id, _]) => [id, 0]),
	);

	const form = useAppForm({
		defaultValues: defaultBidItemValues,
		async onSubmit({ value }) {
			goToPayment({
				individual: {
					bid_items: Object.entries(value).map(([id, quantity]) => ({
						bid_price_id: id,
						quantity,
					})),
				},
			});
		},
	});

	const itemQtys = useStore(form.store, (state) =>
		Object.entries(state.values),
	);
	const bidItemsPrice = itemQtys.reduce((acc, [id, qty]) => {
		const item = uniqBids.get(id);
		const price = item?.price ?? 0;
		return acc + qty * price;
	}, 0);
	const totalPriceExclVat = rebuy
		? bidItemsPrice
		: battle.entry_price + bidItemsPrice;

	return (
		<LayoutGroup id="bid-package-radio-indicator">
			<motion.form
				layout
				onSubmit={(e) => {
					e.preventDefault();
					form.handleSubmit();
				}}
				className="flex flex-col gap-y-6"
			>
				{uniqBids.entries().map(([id, bidItem]) => (
					<form.AppField key={id} name={id}>
						{(field) => (
							<div className="flex gap-2 justify-between items-center">
								<p className="inline-flex gap-2 items-center">
									<strong>+{bidItem.seconds}s</strong>
									<span>{formatCurrency("EUR", bidItem.price)}</span>
								</p>
								<div className="inline-grid grid-cols-3 gap-2 items-center justify-between">
									<Button
										variant="icon"
										className="text-2xl disabled:opacity-50"
										size="content"
										onClick={() =>
											field.handleChange((value) => Math.max(0, value - 1))
										}
										disabled={field.state.value <= 0}
									>
										<MinusCircle size="1em" stroke="currentColor" />
									</Button>
									<span className="inline-flex justify-center text-center">
										{field.state.value}
									</span>
									<Button
										variant="icon"
										className="text-2xl disabled:opacity-50"
										size="content"
										onClick={() => field.handleChange((value) => value + 1)}
									>
										<PlusCircle size="1em" stroke="currentColor" />
									</Button>
								</div>
							</div>
						)}
					</form.AppField>
				))}
				<CheckoutSummary
					rebuy={rebuy}
					entryPrice={battle.entry_price}
					bidsPrice={bidItemsPrice}
					totalPriceExclVat={totalPriceExclVat}
				/>
				<form.AppForm>
					<form.SubscribeButton label="Check Out" />
				</form.AppForm>
			</motion.form>
		</LayoutGroup>
	);
}

function BidPackageCard({
	bidPackage,
	...props
}: Omit<React.ComponentProps<typeof RadioPrimitive.Root>, "value"> & {
	bidPackage: BidPackage;
}) {
	const { rebuy } = Route.useSearch();
	return (
		<RadioPrimitive.Root
			{...props}
			type="button"
			value={bidPackage.id}
			className="
      relative border border-gray-400/80 rounded-lg
      md:px-8 md:py-6 px-4 py-3 flex flex-col items-start gap-y-4
      md:flex-row md:justify-between md:items-center
      cursor-pointer focus-visible:outline-none"
		>
			<RadioPrimitive.Indicator
				render={
					<motion.span
						layout
						layoutId="bid-package-radio-indicator"
						initial={{ scale: 0.95 }}
						animate={{ scale: 1 }}
					/>
				}
				className="absolute inset-0 border-[3px] border-accent-primary rounded-lg"
			/>

			<p className="text-gray-500 text-sm leading-5 inline-flex items-center grow">
				<span className="font-medium text-gray-900 text-2xl leading-[1.875rem] md:text-3xl md:leading-[2.375rem]">
					{bidPackage.total_bids}
				</span>
				&nbsp; Bids
			</p>
			<ul className="list-none text-gray-500 shrink">
				{bidPackage.bid_prices.map((price) => (
					<div key={price.id} className="flex gap-x-2.5 md:gap-x-4">
						<span>{price.quantity}x</span>
						<span>+{price.seconds}s</span>
						<span className="col-span-2 text-start">
							{formatCurrency("EUR", price.price)} /{" "}
							{formatCurrency("EUR", price.total_price)}
						</span>
					</div>
				))}
			</ul>
			<p className="font-bold text-gray-900 text-lg leading-7 grow text-end">
				{bidPackage.is_included && !rebuy
					? "Included"
					: formatCurrency("EUR", bidPackage.total_price)}
			</p>
		</RadioPrimitive.Root>
	);
}

function CheckoutSummary({
	entryPrice,
	totalPriceExclVat,
	bidsPrice,
	rebuy,
}: {
	entryPrice: number;
	totalPriceExclVat: number;
	bidsPrice: number;
	rebuy: boolean;
}) {
	const vat = calculateVAT(totalPriceExclVat);

	return (
		<div className="w-full">
			<div className="grid grid-cols-2 text-gray-800 gap-y-2">
				{!rebuy && (
					<>
						<p>Entry price</p>
						<p className="text-end">{formatCurrency("EUR", entryPrice)}</p>
					</>
				)}
				<>
					<p>Bids</p>
					<p className="text-end">{formatCurrency("EUR", bidsPrice)}</p>
				</>
			</div>
			<Divider className="my-7" />
			<div className="grid grid-cols-2 text-gray-800 gap-y-2">
				<p>Sub total</p>
				<p className="text-end">{formatCurrency("EUR", totalPriceExclVat)}</p>
				<p>VAT ({vat.percentage * 100}%)</p>
				<p className="text-end">{formatCurrency("EUR", vat.vat)}</p>
				<p className="font-bold">Total</p>
				<p className="font-bold text-end">
					{formatCurrency("EUR", totalPriceExclVat + vat.vat)}
				</p>
			</div>
		</div>
	);
}

function PaymentForm(props: StageProps) {
	const { id } = Route.useParams();
	const { rebuy } = Route.useSearch();
	const navigate = Route.useNavigate();

	const payment = usePayments();
	const queryClient = useQueryClient();
	const { data: battle } = useSuspenseQuery(
		BBBackendQueryOptions.fetchBattle({ id }),
	);
	const { data: bidPackages } = useSuspenseQuery(
		BBBackendQueryOptions.listBidPackagesForBattle({ battle_id: id }),
	);
	const { data: bidPrices } = useSuspenseQuery(
		BBBackendQueryOptions.listBidPricesForBattle({ battle_id: id }),
	);
	const uniqBids = new Map(bidPrices.map((price) => [price.id, price]));

	const checkoutPackageMutation = useMutation({
		mutationKey: [BBBackendQueryKeys.battle],
		async mutationFn(value: { bid_package_id: string; card_token?: string }) {
			const bidPackage = bidPackages.find(
				(pkg) => pkg.id === value.bid_package_id,
			);
			if (!bidPackage) {
				return;
			}

			const result = await payment.initiate(
				async ({ redirect_url }) =>
					await BBBackendClient.createCheckoutSession({
						...value,
						redirect_url,
						battle_id: battle.id,
						item_type: rebuy ? "bid_package_purchase" : "battle_entry",
					}),
			);
			if (!result.ok) {
				toast.error(`An error occured: ${result.error.message}`);
				return;
			}

			await handleSuccess({
				id: battle.id,
				rebuy,
				queryClient,
				navigate,
			});
		},
	});

	const checkoutBidsMutation = useMutation({
		mutationKey: [BBBackendQueryKeys.battle],
		async mutationFn(value: {
			bid_items: Array<CheckoutBidItem>;
			card_token?: string;
		}) {
			const nonZeroBidItems = value.bid_items.filter(
				(bid_item) => bid_item.quantity > 0,
			);
			if (nonZeroBidItems.length === 0) {
				return;
			}

			const result = await payment.initiate(
				async ({ redirect_url }) =>
					await BBBackendClient.createCheckoutSession({
						// NOTE: removed card token
						// card_token: value.card_token,
						battle_id: battle.id,
						bid_items: nonZeroBidItems,
						item_type: rebuy ? "bid_purchase" : "battle_entry",
						redirect_url,
					}),
			);
			if (!result.ok) {
				toast.error(`An error occured: ${result.error.message}`);
				return;
			}

			await handleSuccess({
				id,
				rebuy,
				queryClient,
				navigate,
			});
		},
	});

	const packageId = props.stage.form?.package?.package_id;
	const bidPackage = bidPackages.find(
		(bidPackage) => bidPackage.id === packageId,
	);
	const bidPackagePrice =
		bidPackage?.is_included && !rebuy ? 0 : (bidPackage?.total_price ?? 0);
	const packageTotalPriceExclVat = rebuy
		? bidPackagePrice
		: battle.entry_price + bidPackagePrice;

	const bidItems = props.stage.form?.individual?.bid_items;
	const bidItemsPrice =
		bidItems?.reduce((acc, { bid_price_id: id, quantity }) => {
			const item = uniqBids.get(id);
			const price = item?.price ?? 0;
			return acc + quantity * price;
		}, 0) ?? 0;
	const bidsTotalPriceExclVat = rebuy
		? bidItemsPrice
		: battle.entry_price + bidItemsPrice;

	const totalPriceExclVat = packageId
		? packageTotalPriceExclVat
		: bidsTotalPriceExclVat;

	return (
		<>
			<CheckoutSummary
				rebuy={rebuy}
				entryPrice={battle.entry_price}
				bidsPrice={packageId ? bidPackagePrice : bidItemsPrice}
				totalPriceExclVat={totalPriceExclVat}
			/>
			<Divider className="my-7" />
			<MolliePaymentForm
				onSubmitSuccess={async (params) => {
					const formState = props.stage.form?.[props.stage.type];
					if (!formState) return;

					switch (true) {
						case "package_id" in formState: {
							await checkoutPackageMutation.mutateAsync({
								bid_package_id: formState.package_id,
								// NOTE: removed card token
								// card_token: value.card_token,
							});
							break;
						}

						case "bid_items" in formState: {
							await checkoutBidsMutation.mutateAsync({
								...formState,
								// NOTE: removed card token
								// card_token: value.card_token,
							});
							break;
						}
						default: {
							const _: never = formState;
						}
					}
				}}
				cancellable
				onCancel={() => props.backToForm(props.stage.form ?? {})}
			/>
		</>
	);
}
